import { SYSTEM_MODE_HIDDEN_VIEWS, SYSTEM_MODES } from '@silverfort-nodelibs/common/utils/system-mode-config';
import { FEATURE_FLAGS_NAMES, LAYOUT_TABS } from '@silverfort-nodelibs/common/utils/consts';

import useFeature from '../featureFlags/useFeature';
import {
    PricingModulesStatus,
    SYSTEM_MODE,
    usePricingModulesState
} from '../../../../context/contexts/PricingModuleContext';
import { useMemo } from 'react';
import { DisabledDisplayOptions } from './types';
import { HIDDEN_TABS_CONFIG } from './hiddenTabsConfigs';
import { HiddenLockedFeatureDisplayConfig, LockedFeatureDisplayConfig } from '../../../common/LockedFeatures';
import { getDisplay } from './utils';
import { SUBCATEGORIES } from '../../common/categories';

const getTabDisplay = (
    tabId: string,
    pricingModulesState: PricingModulesStatus,
    mode: SYSTEM_MODE
): LockedFeatureDisplayConfig => {
    return getDisplay(tabId, pricingModulesState, mode, HIDDEN_TABS_CONFIG, isTabHiddenBySystemMode);
};

const isTabHiddenBySystemMode = (tabId: string, mode: SYSTEM_MODE) => {
    return (SYSTEM_MODE_HIDDEN_VIEWS[LAYOUT_TABS.SYSTEM_SETUP.ID][mode] as any).tabs.has(tabId);
};

const buildTabsToDisplay = (tabIds: string[], pricingModulesState: PricingModulesStatus, mode: SYSTEM_MODE) => {
    return tabIds.filter((tabId) => {
        const fieldDisplayConfig = getTabDisplay(tabId, pricingModulesState, mode);
        return (
            fieldDisplayConfig.enabled ||
            fieldDisplayConfig.disabledDisplay === DisabledDisplayOptions.TOOLTIP ||
            fieldDisplayConfig.disabledDisplay === DisabledDisplayOptions.SHOWSTOPPER
        );
    });
};

export const useGetTabDisplay = (tabId: string) => {
    const systemMode: SYSTEM_MODE | undefined = useFeature(FEATURE_FLAGS_NAMES.SystemMode);
    const { pricingModulesState } = usePricingModulesState();
    const tabDisplay = useMemo(() => {
        if (pricingModulesState) {
            return getTabDisplay(tabId, pricingModulesState, systemMode ?? SYSTEM_MODES.ON_PREM);
        }
        const hiddenDisplayConfig: HiddenLockedFeatureDisplayConfig = {
            enabled: false,
            disabledDisplay: DisabledDisplayOptions.HIDDEN
        };
        return hiddenDisplayConfig;
    }, [tabId, pricingModulesState, systemMode]);
    return tabDisplay;
};

export const useGetVisibleTabIds = (tabIds?: string[]) => {
    const systemMode: SYSTEM_MODE | undefined = useFeature(FEATURE_FLAGS_NAMES.SystemMode);
    const { pricingModulesState } = usePricingModulesState();
    const tabsToDisplay = useMemo(() => {
        if (pricingModulesState) {
            return buildTabsToDisplay(
                tabIds || Object.values(SUBCATEGORIES).map((subcategory) => subcategory.ID),
                pricingModulesState,
                systemMode ?? SYSTEM_MODES.ON_PREM
            );
        }
        return [];
    }, [tabIds, pricingModulesState, systemMode]);
    return tabsToDisplay;
};

export const useGetTabsTermsToHide = (tabIds?: string[]) => {
    const systemMode: SYSTEM_MODE | undefined = useFeature(FEATURE_FLAGS_NAMES.SystemMode);
    const { pricingModulesState } = usePricingModulesState();
    const fieldsTermsToHide = useMemo(() => {
        if (pricingModulesState) {
            const tabsToCheck = tabIds
                ? Object.values(SUBCATEGORIES).filter((subcategory) => tabIds.includes(subcategory.ID))
                : Object.values(SUBCATEGORIES);
            const tabsToShow = buildTabsToDisplay(
                tabsToCheck.map((subcategory) => subcategory.ID),
                pricingModulesState,
                systemMode ?? SYSTEM_MODES.ON_PREM
            );
            const termsToHide = tabsToCheck
                .filter((tab) => !tabsToShow.includes(tab.ID))
                .map((tab) => [tab.NAME, ...(tab.TAGS || [])])
                .flat();
            return termsToHide;
        }
        return [];
    }, [tabIds, pricingModulesState, systemMode]);
    return fieldsTermsToHide;
};
